import { GridFilterItem, GridSortItem } from '@mui/x-data-grid-pro';

import { httpClient, HttpMethod } from './http-client';
import { AppUser, CustomPermission, LoggedInUser, QueryOption, CSVExportUser } from '../@types';
import { ProfileConfig } from '../pages/Profile/Profile.props';
import { isNotArrayEmpty } from '../utils/array-utils';
import { mapNumericEqualsOperatorForWire } from '../utils/filterModel';

// Response type for user list
export interface UserListResponse {
  previousPage?: number;
  currentPage?: number;
  nextPage?: number;
  total?: number;
  limit?: number;
  data?: AppUser[];
  filterResponse?: CSVExportUser[];
}

// Response type for user creation
interface UserCreateResponse {
  id: number;
  [key: string]: unknown;
}

// Response type for user update
interface UserUpdateResponse {
  id: number;
  success?: boolean;
}

// Payload for email verification update
interface EmailVerificationPayload {
  isEmailVerified: boolean;
}

// Auth user data from login provider
interface AuthUserLoginData {
  given_name?: string;
  family_name?: string;
  // Azure AD / enterprise & social connections don't always send the split
  // given_name/family_name claims — many only provide `name` (full name) or
  // `nickname`. deriveUserNames() falls back through these.
  name?: string;
  nickname?: string;
  email?: string;
  sub?: string;
  blocked?: boolean;
  email_verified?: boolean;
  picture?: string;
  updated_at?: string;
}

/** Users-list filter fields backed by *numeric* (integer) columns on
 *  api-user-mgmt (`user.id`, the sibling node id, the system role id). Their
 *  `equals` must go out as `=` — see mapNumericEqualsOperatorForWire. */
export const USER_NUMERIC_FILTER_FIELDS: ReadonlySet<string> = new Set([
  'id',
  'siblingNodeId',
  'roleId',
]);

export const getUsers = (params: QueryOption): Promise<UserListResponse> => {
  const { pageSize, page, filterModel, sortModel, multiSearchQuery } = params;
  let url = `/users?limit=${pageSize}&page=${page + 1}`;

  if (sortModel && isNotArrayEmpty(sortModel as GridSortItem[], 0)) {
    url += `&order_by=${sortModel[0]?.field}&order_direction=${sortModel[0]?.sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = mapNumericEqualsOperatorForWire(
      field,
      filter?.operator,
      USER_NUMERIC_FILTER_FIELDS
    );
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  // Free-text "search everything" box. A top-level param the BFF forwards to
  // api-user-mgmt, distinct from the field filters. Encoded so values
  // containing `&`/`=`/spaces can't split into extra query params.
  if (multiSearchQuery) {
    url += `&multiSearchQuery=${encodeURIComponent(multiSearchQuery)}`;
  }

  return httpClient<UserListResponse>(url, HttpMethod.Get);
};

export const getUserById = (id: number): Promise<AppUser> => {
  return httpClient<AppUser>(`/users/${id}`, HttpMethod.Get);
};

export const getUserByAuth0Id = (id: string): Promise<AppUser> => {
  return httpClient<AppUser>(`/users/byAuth0Id/${id}`, HttpMethod.Get);
};

export const createUser = (user: Partial<AppUser>): Promise<UserCreateResponse> => {
  return httpClient<UserCreateResponse>('/users', HttpMethod.Post, user);
};

export const updateUser = (id: number, user: Partial<AppUser>): Promise<UserUpdateResponse> => {
  return httpClient<UserUpdateResponse>(`/users/${id}`, HttpMethod.Put, user);
};

export const updateUserEmailVerification = (
  id: number,
  user: EmailVerificationPayload
): Promise<UserUpdateResponse> => {
  return httpClient<UserUpdateResponse>(`/users/${id}/verifyemail`, HttpMethod.Put, user);
};

export const getUserPermissions = (id: number | string): Promise<CustomPermission[]> => {
  return httpClient<CustomPermission[]>(`/users/${id}/permissions`, HttpMethod.Get);
};

export const getUserSystemPermissions = (): Promise<CustomPermission[]> => {
  return httpClient<CustomPermission[]>('/getUserPermissions', HttpMethod.Get);
};

export const createUserPermissions = (
  id: string,
  userPermissions: CustomPermission[]
): Promise<void> => {
  return httpClient<void>(`/users/${id}/permissions`, HttpMethod.Post, { userPermissions });
};

// Placeholder used only when an Auth0 record carries no usable name claim at
// all (no given_name/family_name, name, nickname, or email). An admin can edit
// it afterwards — the point is to never send an empty firstName/lastName, which
// the BFF's /users/activedirectoryuser validator rejects.
const FALLBACK_USER_NAME = 'User';

// First trimmed, non-empty value among the candidates ('' if none).
const firstNonEmpty = (...values: Array<string | undefined>): string =>
  values.map((value) => value?.trim()).find(Boolean) ?? '';

/**
 * Derive a non-empty firstName/lastName from an Auth0 login profile.
 *
 * The BFF's POST /users/activedirectoryuser requires both firstName and
 * lastName to be non-empty. Username/password logins (created via the admin
 * Create User flow) always carry given_name/family_name, but Azure AD /
 * enterprise and social connections frequently omit them and send only `name`
 * (full name) or `nickname`. Without a fallback, those brand-new SSO users are
 * rejected and never get a CMS2 record. Prefer the split claims, then a split
 * of the full name, then nickname, then the email local-part.
 */
export const deriveUserNames = (
  authUser: AuthUserLoginData
): { firstName: string; lastName: string } => {
  const given = authUser.given_name?.trim() ?? '';
  const family = authUser.family_name?.trim() ?? '';
  if (given && family) {
    return { firstName: given, lastName: family };
  }

  const fullName = firstNonEmpty(authUser.name, authUser.nickname, authUser.email?.split('@')[0]);
  const parts = fullName.split(/\s+/).filter(Boolean);

  const firstName = given || parts[0] || FALLBACK_USER_NAME;
  const lastName = family || parts.slice(1).join(' ') || firstName;

  return { firstName, lastName };
};

export const createUserWhenLogin = (authUser: AuthUserLoginData): Promise<UserCreateResponse> => {
  const { firstName, lastName } = deriveUserNames(authUser);
  const user = {
    firstName,
    lastName,
    email: authUser?.email,
    contactEmail: authUser?.email,
    auth0Id: authUser?.sub,
    systemRoleId: 101,
    isActive: authUser?.blocked !== true,
    isLocked: false,
    isEmailVerified: authUser?.email_verified ?? false,
    structureId: 1,
    profilePic: authUser?.picture,
    timezoneId: 1,
    notes: 'Add note',
    // The BFF requires lastActive to be non-empty (sending '' is rejected with
    // a 400, so the user row is never created). /userLogin re-stamps it
    // server-side on the next request, so the exact value here only needs to be
    // a valid, non-empty ISO timestamp.
    lastActive: new Date().toISOString(),
  };

  return httpClient<UserCreateResponse>('/users/activedirectoryuser', HttpMethod.Post, user);
};

export const getLoggedInUserData = (): Promise<LoggedInUser> => {
  return httpClient<LoggedInUser>('/userLogin', HttpMethod.Get);
};

export const updateProfileConfig = (
  id: number,
  payload: { config: ProfileConfig }
): Promise<void> => {
  return httpClient<void>(`/users/profile/${id}`, HttpMethod.Put, payload);
};
